import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import Styles from './mIndex.module.scss';

export interface ICommonTextarea {
  className?: string;
  id: string;
  rows?: number;
  cols?: number;
  maxLength?: number;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  readonly?: boolean;
  focused?: boolean;
  errorMsg?: string;
  wrap?: 'hard' | 'soft';
  autoResize?: boolean;
  enterCreate?: boolean; // 回车创建新 textarea
  enterKeyHint?: string;
  isMaxLength?: boolean;
  onChange?: (contentId: string, val?: string, p?: [number, number]) => void;
  onFocus?: (textId: string) => void;
  onBlur?: (val?: string, p?: [number, number]) => void;
  onCreateNew?: (currentId: string, value: string) => void;
  onDelete?: (textId: string) => void;
  onCombine?: (textId: string, value: string) => void;
}

const CommonTextarea = ({
  id,
  className = '',
  defaultValue = '',
  value = '',
  disabled = false,
  focused = false,
  errorMsg = '',
  autoResize = false,
  enterCreate = false,
  maxLength = 10 * 1000,
  isMaxLength = false,
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  onCreateNew = () => {},
  onDelete = () => {},
  onCombine = () => {},
  ...restProps
}: ICommonTextarea) => {
  const [textareaValue, setTextareaValue] = useState(defaultValue || value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const hiddenTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const heightRef = useRef<string>('auto');
  const [prevTextareaValue, setPrevTextareaValue] = useState(value);

  useEffect(() => {
    setTextareaValue(value);
    if (autoResize) {
      handleResize();
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focused) {
      textareaRef.current?.focus();
    }
  }, [focused]);

  const handleResize = () => {
    setTimeout(() => {
      const textarea = textareaRef.current;
      const hiddenTextarea = hiddenTextareaRef.current;

      if (!textarea) {
        return;
      }
      textarea.style.height = heightRef.current;
      if (!hiddenTextareaRef) {
        return;
      }

      const height = hiddenTextarea?.scrollHeight;
      heightRef.current = `${height}px`;
      textarea.style.height = `${height}px`;
    }, 10);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isMaxLength && e.key !== 'Backspace' && e.keyCode !== 8) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && e.keyCode === 13) {
      // 回车：创建新文本框
      onCreateNew(id, textareaValue);
    } else if ((e.key === 'Backspace' || e.keyCode === 8) && !textareaValue) {
      // 回退键：若文本框为空，则删除该文本框
      e.preventDefault();
      onDelete(id);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    if (isMaxLength && val.length >= prevTextareaValue.length) {
      e.preventDefault();
      return;
    }

    if (val === '\n') {
      return ;
    }

    setTextareaValue(val);
    if (autoResize) {
      handleResize();
    }

    onChange(id, val);
    setPrevTextareaValue(val);
  };

  const handleBlur = () => {
    textareaRef.current?.blur();
    onBlur(textareaValue);
    onChange(id, textareaValue);
  };

  const handleFocus = () => {
    textareaRef.current?.focus();
    onFocus(id);
  };

  return (
    <div className={cx(Styles.root, className, {
      [Styles.focused]: focused,
      [Styles.disabled]: disabled,
      [Styles.error]: textareaValue.length >= maxLength,
    })}>
      <textarea
        ref={textareaRef}
        className={Styles.textarea}
        onKeyDown={handleKeydown}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        value={textareaValue}
        maxLength={maxLength}
        {...restProps}
      />
      {
        autoResize && <textarea ref={hiddenTextareaRef} className={Styles.hidden} rows={1} value={textareaValue} aria-hidden readOnly />
      }
      {
        textareaValue.length >= maxLength && <div className={Styles.error}>{errorMsg}</div>
      }
    </div>
  );
};

export default CommonTextarea;
